import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { apiClient } from '../../shared/api/client'
import type { AuthUser } from '@crm/shared'

interface AuthState {
  user: AuthUser | null
  isAuthenticated: boolean
  /** false при первой загрузке страницы — пока не завершён fetchMe() */
  isAuthChecked: boolean
  /**
   * Проверка сессии не получила ответа сервера (нет сети, VPN, таймаут, 5xx).
   * Это не выход: ProtectedRoute показывает «Нет связи» с кнопкой «Повторить».
   */
  connectionLost: boolean
  /** Сервер ответил «слишком много запросов» (429) — текст «подождите», а не «нет связи». */
  serverBusy: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  fetchMe: () => Promise<void>
}

/**
 * Счётчик «поколения» сессии. Растёт на каждом входе и выходе; ответ fetchMe,
 * запущенного в прошлом поколении, применять уже нельзя.
 *
 * Раньше отказ от /auth/me безусловно ставил user=null. Проверка сессии
 * стартует при загрузке страницы, и её 401 мог прийти уже ПОСЛЕ успешного
 * входа — человек логинился и тут же оказывался на странице входа снова
 * (ловилось E2E-прогонами).
 */
let authGeneration = 0

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isAuthChecked: false,
      connectionLost: false,
      serverBusy: false,

      login: async (email, password) => {
        const { data } = await apiClient.post<{ success: true; data: { user: AuthUser } }>(
          '/auth/login',
          { email, password },
        )
        authGeneration += 1
        set({
          user: data.data.user,
          isAuthenticated: true,
          isAuthChecked: true,
          connectionLost: false,
        })
        const { useRealtimeStore } = await import('../../shared/realtime/realtimeStore')
        useRealtimeStore.getState().connect()
      },

      logout: async () => {
        const { useRealtimeStore } = await import('../../shared/realtime/realtimeStore')
        useRealtimeStore.getState().disconnect()
        try {
          await apiClient.post('/auth/logout')
        } catch {
          // API error on logout is non-critical — clear state regardless
        }
        authGeneration += 1
        set({ user: null, isAuthenticated: false, isAuthChecked: true, connectionLost: false })
        window.location.replace('/login')
      },

      fetchMe: async () => {
        const startedAt = authGeneration
        try {
          // 10 с вместо общих 30: при «висящей» сети экран «Нет связи» появляется быстро.
          const { data } = await apiClient.get<{ success: true; data: AuthUser }>('/auth/me', {
            timeout: 10_000,
          })
          if (startedAt !== authGeneration) return
          set({
            user: data.data,
            isAuthenticated: true,
            isAuthChecked: true,
            connectionLost: false,
          })
          const { useRealtimeStore } = await import('../../shared/realtime/realtimeStore')
          useRealtimeStore.getState().connect()
        } catch (e) {
          // Ответ устарел: пока запрос шёл, человек успел войти или выйти.
          // Применять его — значит выбросить свежую сессию на /login.
          if (startedAt !== authGeneration) return
          // Выход — только когда сервер ответил «сессии нет» (401/403). Раньше
          // любой сбой связи при открытии сайта выбрасывал на вход (19.10.2026,
          // телефон через VPN): сессия жива, а человек снова вводит пароль.
          const status = (e as { response?: { status?: number } } | null)?.response?.status
          if (status === 401 || status === 403) {
            set({ user: null, isAuthenticated: false, isAuthChecked: true, connectionLost: false })
            return
          }
          // Человек уже внутри (повторная проверка не прошла) — оставляем как есть.
          if (get().user) return
          // 429 — сервер ответил «слишком много запросов»: связь есть, просто подождать.
          set({ isAuthChecked: true, connectionLost: true, serverBusy: status === 429 })
        }
      },
    }),
    {
      name: 'crm-auth',
      // isAuthChecked намеренно НЕ персистируется — при каждой загрузке страницы
      // нужно заново проверить сессию через fetchMe()
      partialize: (state) => ({ isAuthenticated: state.isAuthenticated }),
    },
  ),
)
